import 'projects/sharedComponents/src/pet/careResources/health/careResourcesHealthCard.scss';

import { FC, useMemo } from 'react';

import { CareResourcesHealthFragment } from 'projects/sharedComponents/src/generated/graphql-client-types';
import { CareResourcesSectionCarouselItem } from 'projects/sharedComponents/src/pet/careResources/careResourcesSectionCarouselItem';
import { Carousel } from 'projects/sharedComponents/src/carousel/carousel';
import { Typography } from '@mui/material';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { handleGraphQLError } from 'projects/sharedComponents/src/services/errors/handleGraphQLError';
import { richTextDefaultOptions } from 'projects/sharedComponents/src/pet/careResources/contentful/richTextDefaultOptions';
import { useGetSectionForHealthConditionQuery } from 'projects/sharedComponents/src/generated/contentful-graphql-types';

export type CareResourcesSummaryPageProps = {
  pet: CareResourcesHealthFragment;
  conditionId: string;
};

const emptyArray: [] = [];

export const CareResourcesHealthCard: FC<CareResourcesSummaryPageProps> = ({ pet, conditionId }) => {
  const { data, loading, error } = useGetSectionForHealthConditionQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
    variables: {
      conditionId,
    },
  });

  const condition = data?.healthConditionCollection?.items?.[0];
  const section = condition?.careResourcesSectionsCollection?.items?.[0];

  // Ensures the pet would be seeing this section in care resources
  const healthConditionTarget = useMemo(
    () =>
      pet.careResources?.targets.find((resource) => {
        return resource.sectionId === section?.sectionId;
      }),
    [pet, section]
  );

  const reasonId = healthConditionTarget?.reasonId;
  const possiblePersonalizedTexts = section?.personalizedTextCollection?.items ?? emptyArray;

  const reason = useMemo(() => {
    return possiblePersonalizedTexts.find((personalizedText) => personalizedText?.reason?.reasonId === reasonId);
  }, [possiblePersonalizedTexts, reasonId]);

  if (error) {
    handleGraphQLError(error);
    return <></>;
  }

  if (loading || !data) {
    return <></>;
  }

  if (!section || !section.sectionId) {
    return <></>;
  }

  if (!healthConditionTarget) {
    return <></>;
  }

  const itemsInCarousel = section.itemsCollection?.items ?? [];

  return (
    <>
      <div className="gray-spacer"></div>
      <div className="careResourcesHealthCard-container">
        <Typography className="careResourcesHealthCard-title">Recommended for {pet.name}</Typography>
        {!!reason?.personalizedText && (
          <div className="careResourcesHealthCard-description">
            {documentToReactComponents(reason.personalizedText.json, richTextDefaultOptions(pet))}
          </div>
        )}
        <Typography className="careResourcesHealthCard-disclosure">
          Disclosure: Product recommendations contain affiliate links, meaning that Embark may receive a small
          commission if you make a purchase. All linked products have been reviewed by a veterinarian.
        </Typography>
        <div>
          <Carousel scrollOptions={{ scrollOnePage: true }} spacing={4.1}>
            {itemsInCarousel.map((item, index) => (
              <CareResourcesSectionCarouselItem
                key={index}
                item={item}
                pet={pet}
                reasonId={reasonId}
                index={index}
                useReactRouterLink={false}
              />
            ))}
          </Carousel>
        </div>
      </div>
    </>
  );
};
